import { ConfirmActionModal } from '@shared/components';
import { formatMinorAmount } from '../utils/formatScheduleCurrency';
import type { PriceChangeConfirmationState } from '../hooks/usePriceChangeConfirmation';

export interface PriceChangedModalProps {
    readonly state: PriceChangeConfirmationState;
    readonly isPending: boolean;
    readonly onConfirm: () => void;
    readonly onClose: () => void;
}

const buildMessage = (state: PriceChangeConfirmationState): string => {
    const fresh = formatMinorAmount(state.freshTotalMinor ?? undefined, 'GBP');
    if (state.previousTotalMinor === null) {
        return `The total for this quote is ${fresh} including VAT. Save the quote at this price?`;
    }
    const previous = formatMinorAmount(state.previousTotalMinor, 'GBP');
    return `The total for this quote has changed from ${previous} to ${fresh} including VAT. Save the quote at the new price?`;
};

/** Confirms a server-side `price_changed` refusal before re-saving at the new total. A second
 *  refusal only rewrites the message, so it is announced from a live region, not on open. */
export const PriceChangedModal: React.FC<PriceChangedModalProps> = ({
    state,
    isPending,
    onConfirm,
    onClose,
}) => (
    <ConfirmActionModal
        isOpen={state.isOpen}
        title="The price has changed"
        message={
            <p role="status" aria-live="polite">
                {buildMessage(state)}
            </p>
        }
        confirmLabel="Save at new price"
        confirmVariant="primary"
        isPending={isPending}
        onConfirm={onConfirm}
        onClose={onClose}
    />
);
